<template>
    <div class="view-approvals px-0 pt-3 pb-0">
        <!-- no open registrations and contract submissions -->
        <template v-if="participantApprovals.length <= 0 && contractClaimApprovals.length <= 0">
            <v-card class="px-7 py-2" flat tile>
                <h2 class="headline mb-0">
                    <template v-if="!isFetchingContracts && !isFetchingParticipantApprovals">
                        {{ $vuetify.lang.t('$vuetify.approval.complete_headline') }}
                    </template>
                    <v-skeleton-loader type="heading" v-else/>
                </h2>
                <div class="my-7 text-center">
                    <v-icon large color="success"
                            v-if="!isFetchingContracts && !isFetchingParticipantApprovals">mdi-check-all
                    </v-icon>
                    <v-skeleton-loader type="avatar" v-else class="d-flex justify-center"/>
                </div>
                <p class="mb-2 body-1">
                    <template v-if="!isFetchingContracts && !isFetchingParticipantApprovals">
                        {{ $vuetify.lang.t('$vuetify.approval.complete_desc') }}
                    </template>
                    <v-skeleton-loader type="sentences" v-else/>
                </p>
            </v-card>
        </template>
        <!-- /no open registrations and contract submissions -->

        <!-- open registrations and contract submissions -->
        <template v-else>

            <!-- what to check text -->
            <v-card class="px-7 py-2" flat tile>
                <template v-if="!isFetchingContracts && !isFetchingParticipantApprovals">
                    <h1 class="headline font-weight-bold mb-2">
                        {{ $vuetify.lang.t('$vuetify.approval.headline') }}
                    </h1>
                    <p class="body-1 mb-2">
                        {{ $vuetify.lang.t('$vuetify.approval.desc') }}
                    </p>
                </template>
                <template v-else>
                    <v-skeleton-loader type="heading" class="mb-2"/>
                    <v-skeleton-loader type="sentences"/>
                </template>
            </v-card>
            <!-- /what to check text -->

            <!-- participant approvals -->
            <v-card v-if="participantApprovals.length > 0" class="mt-2 px-7 pt-2 pb-2" flat tile>
                <h2 class="headline font-weight-bold mb-2">
                    <template v-if="!isFetchingParticipantApprovals">
                        {{ $vuetify.lang.t('$vuetify.approval.participant_headline') }}
                    </template>
                    <v-skeleton-loader type="heading" v-else/>
                </h2>
                <p class="body-1 mb-2">
                    <template v-if="!isFetchingParticipantApprovals">
                        {{ $vuetify.lang.t('$vuetify.approval.participant_desc') }}
                    </template>
                    <v-skeleton-loader type="sentences" v-else/>
                </p>

                <template v-if="!isFetchingParticipantApprovals">
                    <v-slide-x-transition group :leave-absolute="true" pa-0>
                        <approve-participant-card v-for="approval in participantApprovals"
                                                  :key="approval.id"
                                                  :approval="approval"
                                                  :reasons="reasonsParticipant"
                                                  :approve="approveParticipant"
                                                  :decline="declineParticipant"/>
                    </v-slide-x-transition>
                </template>

                <!-- skeleton loading -->
                <template v-else>
                    <v-card
                            class="mb-2"
                            flat
                            :key="index"
                            tile
                            v-for="(item, index) in participantApprovals">
                        <v-skeleton-loader type="article"/>
                    </v-card>
                </template>
                <!-- /skeleton loading -->
            </v-card>
            <!-- /participant approvals -->

            <!-- contract submissions approvals -->
            <v-card v-if="contractClaimApprovals.length > 0"
                    class="mt-2 px-7 pt-2 pb-2"
                    flat
                    tile>
                <!-- contracts -->
                <h2 class="headline font-weight-bold mb-2">
                    <template v-if="!isFetchingParticipantApprovals">
                        {{ $vuetify.lang.t('$vuetify.approval.contract_claim_headline') }}
                    </template>
                    <v-skeleton-loader type="heading" v-else/>
                </h2>
                <p class="body-1 mb-2">
                    <template v-if="!isFetchingParticipantApprovals">
                        <div v-html="$vuetify.lang.t('$vuetify.approval.contract_claim_desc')"></div>
                    </template>
                    <v-skeleton-loader type="sentences" v-else/>
                </p>

                <template v-if="!isFetchingParticipantApprovals">
                    <v-slide-x-transition group :leave-absolute="true" pa-0>
                        <approve-contract-card v-for="approval in contractClaimApprovals"
                                               :key="approval.id"
                                               :approval="approval"
                                               :reasons="reasonsContractClaim"
                                               :approve="approveContract"
                                               :decline="declineContract"/>
                    </v-slide-x-transition>
                </template>

                <!-- skeleton loading -->
                <template v-else>
                    <v-card
                            class="mb-2"
                            flat
                            :key="index"
                            tile
                            v-for="(item, index) in participantApprovals">
                        <v-skeleton-loader type="article"/>
                    </v-card>
                </template>
                <!-- /skeleton loading -->
            </v-card>
            <!-- /contract submissions approvals -->

        </template>
        <!-- /open registrations and contract submissions -->

    </div>
</template>

<script>
    // components
    import ApproveParticipantCard from '../../components/Cards/ApproveParticipant';
    import ApproveContractCard from '../../components/Cards/ApproveContract';
    // models
    import ParticipantApproval from '../../models/ParticipantApproval';
    import Contract from '../../models/Contract';
    // utils
    import {
        LOADER_FETCH_APPROVALS,
        LOADER_FETCH_CONTRACT_APPROVALS,
    } from '../../utils/constants';
    // utils
    import { setPageTitle } from '../../utils/helper';

    export default {
        name: 'approvals',
        components: {
            ApproveContractCard,
            ApproveParticipantCard,
        },
        data() {
            return {
                reasonsParticipant: [],
                reasonsContractClaim: [],
            }
        },
        computed: {
            participantApprovals() {
                return ParticipantApproval.query().orderBy('created_at', 'desc').withAll().get();
            },
            contractClaimApprovals() {
                return Contract.getApprovals();
            },
            isFetchingContracts() {
                return this.$wait.is(LOADER_FETCH_CONTRACT_APPROVALS);
            },
            isFetchingParticipantApprovals() {
                return this.$wait.is(LOADER_FETCH_APPROVALS);
            },
        },
        methods: {
            approveParticipant(approvalId, userId) {
                ParticipantApproval.approve(approvalId, userId);
            },
            declineParticipant(approvalId, userId, reasonId) {
                ParticipantApproval.decline(approvalId, userId, reasonId);
            },
            approveContract(approvalId) {
                Contract.approve(approvalId);
            },
            declineContract(approvalId, reasonId) {
                Contract.decline(approvalId, reasonId);
            },
        },
        async mounted() {
            // reasons participant
            const reasonsParticipant = await ParticipantApproval.fetch();
            reasonsParticipant && (this.reasonsParticipant = reasonsParticipant);

            // reasons contract claim
            const reasonsContractClaim = await Contract.fetchApprovals();
            reasonsContractClaim && (this.reasonsContractClaim = reasonsContractClaim);
        },
    }
</script>
